import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Keeps a tab bar at the top of the screen while scrolling, for use in Tabs' renderTabBar.
 * CSS sticky can't be used because .layout-dashboard sets overflow, and antd only styles
 * a tab bar nested one wrapper deep, so this is a single fixed wrapper plus a spacer that
 * marks where the bar normally sits.
 */
export default function StickyTabBar({ anchorRef, children }) {
  const contentRef = useRef(null);
  const [pinned, setPinned] = useState(null);

  const updatePinned = useCallback(() => {
    if (!anchorRef.current || !contentRef.current) return;

    const { top, left, width } = anchorRef.current.getBoundingClientRect();
    const height = contentRef.current.offsetHeight;

    setPinned((current) => {
      if (top >= 0) return null;
      if (current?.left === left && current?.width === width && current?.height === height) return current;
      return { left, width, height };
    });
  }, [anchorRef]);

  useEffect(() => {
    updatePinned();

    // Capture so scrolling inside the layout is seen as well as scrolling the page
    document.addEventListener('scroll', updatePinned, { capture: true, passive: true });
    window.addEventListener('resize', updatePinned);
    const resizeObserver = window.ResizeObserver ? new window.ResizeObserver(updatePinned) : null;
    resizeObserver?.observe(contentRef.current);

    return () => {
      document.removeEventListener('scroll', updatePinned, { capture: true });
      window.removeEventListener('resize', updatePinned);
      resizeObserver?.disconnect();
    };
  }, [updatePinned]);

  return (
    <>
      <div ref={anchorRef} style={{ flex: 'none', height: pinned?.height ?? 0 }} />
      <div
        ref={contentRef}
        style={{
          display: 'flow-root',
          ...(pinned && {
            position: 'fixed',
            top: 0,
            left: pinned.left,
            width: pinned.width,
            zIndex: 10,
            background: '#fff',
          }),
        }}>
        {children}
      </div>
    </>
  );
}
